import type { ShotFrameSource } from "@/lib/workspace-api"
import {
  FLOW_AUDIO_PORT,
  FLOW_FRAMES_PORT,
  FLOW_IMAGE_SOURCE,
  FLOW_IMAGE_TARGET,
  FLOW_LINKS_PORT,
  FLOW_VIDEO_PORT,
  type FlowNodeKind,
  type FlowNodeRow,
} from "@/types/flow"
import {
  flowAttachSingle,
  flowAttachWires,
  flowConnectors,
  type FlowConnectorNode,
  type FlowWire,
} from "@/types/flow-connectors"

/** Как у ShotLinks: новая нода встаёт слева от цели, следующая — ступенькой ниже. */
export const FLOW_ATTACH_PLACE_X = 440
export const FLOW_ATTACH_PLACE_STEP = 200

export type FlowAttachMedia = "image" | "video" | "audio"

export type FlowAttachPorts = {
  sourcePort: typeof FLOW_IMAGE_SOURCE | typeof FLOW_VIDEO_PORT | typeof FLOW_AUDIO_PORT
  targetPort: typeof FLOW_IMAGE_TARGET | typeof FLOW_LINKS_PORT | typeof FLOW_VIDEO_PORT | typeof FLOW_FRAMES_PORT
}

export type FlowAttachPick = {
  source: ShotFrameSource
  media: FlowAttachMedia
  label: string
}

export type FlowAttachRequest = {
  files?: File[]
  picks?: readonly FlowAttachPick[]
  /** После провода положить новый ассет именно в этот слот. */
  slotIndex?: number
  /** Положить файл в этот порт, а не в первый свободный. */
  targetPort?: FlowAttachPorts["targetPort"]
}

export type FlowAttachOutcome = {
  attached: number
  failed: number
}

type AttachTarget = Pick<FlowNodeRow, "kind" | "inputs" | "position_x" | "position_y">

/** Что эта нода вообще может принять файлом. Пустой список — кнопок нет. */
export function flowAttachMediaForTarget(kind: FlowNodeKind): FlowAttachMedia[] {
  return flowAttachWires(kind)
}

/** У улучшения видео вход один. Остальные ноды принимают пачку. */
export function flowAttachLimit(kind: FlowNodeKind, inputs?: FlowNodeRow["inputs"]): number | null {
  if (kind === "video_transition") {
    const used = (inputs ?? []).filter((input) => input.target_port === FLOW_FRAMES_PORT).length
    return Math.max(0, 2 - used)
  }
  return flowAttachSingle(kind) ? 1 : null
}

export function flowAttachMediaFromContentType(contentType: string | null | undefined): FlowAttachMedia | null {
  const type = contentType ?? ""
  if (type.startsWith("image/")) return "image"
  if (type.startsWith("video/")) return "video"
  if (type.startsWith("audio/")) return "audio"
  return null
}

export function flowAttachPorts(
  target: Pick<FlowNodeRow, "kind" | "inputs" | "chain_slots">,
  media: FlowAttachMedia,
  preferredPort?: FlowAttachPorts["targetPort"],
): FlowAttachPorts | null {
  const node = attachConnectorNode(target)
  const connector = flowConnectors(node).find((item) => {
    if (preferredPort != null && item.port !== preferredPort) return false
    if (item.side !== "target" || !item.types.some((wire) => attachWires(target.kind, media).includes(wire))) {
      return false
    }
    // У цепочки слотов видео может быть больше одного, а связь всё равно сажает один клип.
    // Кнопка файла смотрит на слот, не на уже занятый клип.
    if (target.kind === "chain" || item.max == null) return true
    const used = (target.inputs ?? []).filter((input) => (input.target_port ?? FLOW_IMAGE_TARGET) === item.port).length
    return used < item.max
  })
  if (!connector) return null
  const sourcePort = media === "video" ? FLOW_VIDEO_PORT : media === "audio" ? FLOW_AUDIO_PORT : FLOW_IMAGE_SOURCE
  return { sourcePort, targetPort: connector.port as FlowAttachPorts["targetPort"] }
}

function attachWires(kind: FlowNodeKind, media: FlowAttachMedia): FlowWire[] {
  if (media === "audio") return ["audio"]
  if (media === "image") return ["image"]
  return kind === "shot" ? ["shot_video"] : ["video"]
}

function attachConnectorNode(target: Pick<FlowNodeRow, "kind" | "inputs" | "chain_slots">): FlowConnectorNode {
  return {
    id: 0,
    kind: target.kind,
    inputs: target.inputs,
    chain_id: target.kind === "chain" ? 1 : null,
    chain_slots: target.chain_slots ?? [],
  }
}

export function flowAttachPosition(target: AttachTarget, index: number): { positionX: number; positionY: number } {
  const base = target.inputs?.length ?? 0
  return {
    positionX: target.position_x - FLOW_ATTACH_PLACE_X,
    positionY: target.position_y + (base + index) * FLOW_ATTACH_PLACE_STEP,
  }
}
